import { useState, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useAngularContext } from 'AngularContext';
import { userActionsApi } from 'Users';
import { type ProgramSwitcherProgramConfig } from 'ProgramSwitcher';
import { type ProgramSwitcherPresentationProps } from './ProgramSwitcher.types';

type ProgramSwitcherProps = {
    programConfigs: ProgramSwitcherProgramConfig[];
    onProgramSwitch?: () => void;
};

const isQuantic = (styleClass: string) => styleClass === 'quantic' || styleClass === 'smartly';
const isValar = (styleClass: string) => styleClass === 'valar';

// oxlint-disable-next-line complexity
function ProgramRow({
    programConfig,
    isSwitching,
    onSwitchProgram,
}: {
    programConfig: ProgramSwitcherProgramConfig;
    isSwitching: boolean;
    onSwitchProgram: (config: ProgramSwitcherProgramConfig) => void;
}) {
    const { selected, hiddenByDefault } = programConfig;
    const disabled = isSwitching;

    return (
        <div
            className={clsx(
                // Test compatibility class
                'program-section',
                programConfig.styleClass,
                selected && 'selected',
                // Base styles
                'flex',
                'items-center',
                'py-3.5',
                'px-4',
                'border-0',
                'border-b',
                'border-solid',
                'border-slate-grey-200',
                // Cursor and hover
                !selected && !disabled && 'cursor-pointer',
                !selected && !disabled && 'hover:bg-slate-grey-100',
                'transition-colors',
                // Selected state - pointer-events none and background color
                selected && !disabled && 'pointer-events-none',
                selected && !disabled && isQuantic(programConfig.styleClass) && 'bg-coral/5',
                selected && !disabled && isValar(programConfig.styleClass) && 'bg-blue/5',
                // Disabled state
                disabled && 'bg-black/5',
            )}
            onClick={() => !isSwitching && onSwitchProgram(programConfig)}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (!isSwitching) onSwitchProgram(programConfig);
                }
            }}
        >
            <img
                src={programConfig.menuLogo}
                alt={programConfig.programTitle}
                className={clsx(
                    'max-h-[25px]',
                    'w-[25px]',
                    'flex-shrink-0',
                    'me-3',
                    // Grayscale and opacity for non-selected, disabled, or hidden-by-default
                    (!selected || disabled) && 'grayscale',
                    (!selected || disabled) && !hiddenByDefault && 'opacity-50',
                    hiddenByDefault && 'opacity-30',
                )}
            />
            <p
                className={clsx(
                    'leading-[19px]',
                    'p-0',
                    'm-0',
                    'flex-1',
                    'whitespace-normal',
                    hiddenByDefault ? 'text-gray-400' : 'text-gray-700',
                )}
            >
                {programConfig.programTitle}
            </p>
        </div>
    );
}

// Presentation component - renders program list based on explicit props
// oxlint-disable-next-line max-lines-per-function
export function ProgramSwitcherPresentation({
    programConfigs,
    isSwitching,
    onSwitchProgram,
}: ProgramSwitcherPresentationProps) {
    const { t } = useTranslation();
    const [showMore, setShowMore] = useState(false);

    const { visibleConfigs, hiddenConfigs } = useMemo(() => {
        const visible: ProgramSwitcherProgramConfig[] = [];
        const hidden: ProgramSwitcherProgramConfig[] = [];
        for (const config of programConfigs) {
            if (config.hiddenByDefault) {
                hidden.push(config);
            } else {
                visible.push(config);
            }
        }
        return { visibleConfigs: visible, hiddenConfigs: hidden };
    }, [programConfigs]);

    if (programConfigs.length === 0) return null;

    const hasHiddenPrograms = hiddenConfigs.length > 0;

    return (
        <div className="col-span-2 m-0">
            {visibleConfigs.map(programConfig => (
                <ProgramRow
                    key={programConfig.userProgramFieldStateId}
                    programConfig={programConfig}
                    isSwitching={isSwitching}
                    onSwitchProgram={onSwitchProgram}
                />
            ))}

            {hasHiddenPrograms && (
                <div
                    className={clsx(
                        'col-span-2',
                        'grid',
                        'transition-[grid-template-rows]',
                        'duration-200',
                        'ease-in-out',
                        showMore ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                    )}
                >
                    <div className="overflow-hidden">
                        {hiddenConfigs.map(programConfig => (
                            <ProgramRow
                                key={programConfig.userProgramFieldStateId}
                                programConfig={programConfig}
                                isSwitching={isSwitching}
                                onSwitchProgram={onSwitchProgram}
                            />
                        ))}
                    </div>
                </div>
            )}

            {hasHiddenPrograms && (
                <div
                    className={clsx(
                        'py-3.5',
                        'px-4',
                        'text-center',
                        'cursor-pointer',
                        'outline-none',
                        'text-sm',
                        'text-slate-grey',
                        'transition-colors',
                        'hover:bg-slate-grey-100',
                    )}
                    onClick={() => setShowMore(prev => !prev)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setShowMore(prev => !prev);
                        }
                    }}
                >
                    {showMore
                        ? t('navigation.app_menu_user.hide_past_applications')
                        : t('navigation.app_menu_user.show_past_applications')}
                </div>
            )}
        </div>
    );
}

// Container component - handles hooks and state, delegates rendering to presentation
export function ProgramSwitcher({ programConfigs, onProgramSwitch }: ProgramSwitcherProps) {
    const [isSwitching, setIsSwitching] = useState(false);
    const [changePrefProgram] = userActionsApi.useChangePrefProgramMutation();
    const angularContext = useAngularContext();

    const switchProgram = useCallback(
        async (programConfig: ProgramSwitcherProgramConfig) => {
            if (programConfig.selected || isSwitching) return;

            setIsSwitching(true);

            if (onProgramSwitch) {
                onProgramSwitch();
            }

            try {
                await changePrefProgram({
                    user_program_field_state_id: programConfig.userProgramFieldStateId,
                }).unwrap();

                // oxlint-disable-next-line @typescript-eslint/no-explicit-any
                const $injector = (angularContext as any)?.$injector;

                // Replicate refreshUserProgramState: refresh content cache, navigate to dashboard, reload
                const LearnerContentCache = $injector?.get('LearnerContentCache');
                if (LearnerContentCache) await LearnerContentCache.ensureStudentDashboard();

                $injector?.get('Navigation.NavigationHelperMixin')?.loadRoute('/dashboard');
                $injector?.get('reactRouterBridge')?.reload();

                // Hide alerts after switching
                $injector?.get('DialogModal')?.hideAlerts();
            } finally {
                setIsSwitching(false);
            }
        },
        [changePrefProgram, angularContext, isSwitching, onProgramSwitch],
    );

    return (
        <ProgramSwitcherPresentation
            programConfigs={programConfigs}
            isSwitching={isSwitching}
            onSwitchProgram={switchProgram}
        />
    );
}
